import './helper/PatchReactstrapFadeDefaults';
import * as React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { configure } from 'mobx';
import Layout from '../pages/layout/Layout';
import { setAppContext, IPanelAppContext, Notification, AppConfig } from './AppContext';
import { confirm } from '~/components/ConfirmModalHost';
import { showError } from '~/components/ErrorModalHost';
import ApiClient from './ApiClient';
import { Router } from 'react-router-dom';
import { BrowserHistory, createBrowserHistory, Update } from 'history';
import { isAbortError } from './helper/ErrorHelper';
import { EventEmitter } from './helper/EventHelper';
import { AccountStore } from './store/AccountStore';
import { SupportCodeStore } from './store/SupportCodeStore';
import { Account } from './ApiModel';
import ErrorBoundary from '~/components/ErrorBoundary';
import { SiteServicesApiClient, SiteServicesEndpoint } from './SiteServicesApiClient';
import LoginLayout from '~/pages/layout/PreLoginLayout';
import KeepAlive from '~/components/KeepAlive';
import LoginRedirectPage from '~/pages/login/LoginRedirect';
import {
    isInternalNavigation,
    isWithinSection,
    LoginRoute,
    LogoutRoute,
    RegistrationRoute,
    SignupRoute,
    SupportRoute,
} from './helper/RouteHelper';
import { createAuthRedirect } from './helper/AuthRedirect';
import { loginZendeskMessenger } from './helper/ZendeskHelper';

/** A custom browser router that accepts its own history */
function CustomBrowserRouter({
    children,
    history,
    basename,
}: {
    children?: React.ReactNode;
    history: BrowserHistory;
    basename?: string;
}) {
    const [state, dispatch] = React.useReducer((_: Update, action: Update) => action, {
        action: history.action,
        location: history.location,
    });

    React.useLayoutEffect(() => history.listen(dispatch), [history]);

    return (
        <Router
            children={children}
            navigationType={state.action}
            location={state.location}
            navigator={history}
            basename={basename}
        />
    );
}

// TypeScript declaration for build-time process.env substitution
declare const process: {
    env: {
        NODE_ENV: string;
    };
};

//setup some warnings for mobx
configure({
    enforceActions: 'observed',
    useProxies: 'never',
    ...(process.env.NODE_ENV === 'development'
        ? {
              computedRequiresReaction: false,
              reactionRequiresObservable: true,
              observableRequiresReaction: false,
              disableErrorBoundaries: true,
          }
        : {}),
});

type PageData<T> = {
    basePath: string;
    appConfig: AppConfig;
    preload: T;
};

export interface IPanelPageProps {
    appContext: IPanelAppContext;
}

/**
 * Props for the layout wrapper around a page, which renders the page as its children.
 * Kept separate from IPanelPageProps so page components do not silently accept children.
 */
export interface IPanelLayoutProps extends IPanelPageProps {
    children?: React.ReactNode;
}

export function getPageData<T>(): PageData<T> {
    const pageDataElement = document.getElementById('__PAGE_DATA__');
    if (!pageDataElement) {
        throw Error('Missing page data element');
    }
    //use getAttribute to support IE<11
    const dataJson = pageDataElement.getAttribute('data-pagedata');
    if (!dataJson) {
        throw Error('Missing page data');
    }
    return JSON.parse(dataJson) as PageData<T>;
}

type NavigateFunction = (url: string, replace?: boolean) => void;

export function initAppContext(
    appConfig: AppConfig,
    navigate: NavigateFunction,
    onAuthRequired: () => void,
    skipAccountLoad = false
): IPanelAppContext {
    //build application context from page data
    const apiClient = new ApiClient('/api/v2', appConfig.csrfToken, onAuthRequired);
    const siteApiClient = new SiteServicesApiClient(SiteServicesEndpoint, appConfig.csrfToken, onAuthRequired);

    const accountStore = new AccountStore(apiClient);
    if (!skipAccountLoad) {
        void accountStore.load();
    }

    const appContext: IPanelAppContext = {
        config: appConfig,
        apiClient: apiClient,
        siteApiClient: siteApiClient,
        customer: accountStore,
        supportCode: new SupportCodeStore(siteApiClient),
        navigate: navigate,
        notify: new EventEmitter<Notification>(),
        announce: new EventEmitter<string>(),
        isTheme: (theme) => appConfig.theme === theme,
        isEnabled: (feature) => appConfig.enabledFeatures.indexOf(feature) > -1,
        showSearch: new EventEmitter<boolean>(),
        confirm: confirm,
        error: showError,
    };

    setAppContext(appContext);
    return appContext;
}

/**
 * Whether a page never reads the customer's account: the pre-login pages, which have no session, the
 * support section, which only hands off to the external support portal, and logout, which ends the session.
 * Turns off the account load, the Zendesk messenger login that depends on it, and the KeepAlive poll — none
 * of which these pages use, since each shows at most a single card before handing off, and any of which
 * would race the logout. It also suppresses the finish-signup redirect, which is what keeps the support
 * hand-off reachable for an account that has not completed signup. Whether a 401 bounces through login is
 * `createAuthRedirect`'s decision, driven by each page's own API call.
 */
export function skipsAccountLoad(basePath: string): boolean {
    return (
        isWithinSection(basePath, LoginRoute) ||
        isWithinSection(basePath, RegistrationRoute) ||
        isWithinSection(basePath, SupportRoute) ||
        isWithinSection(basePath, LogoutRoute)
    );
}

/**
 * Whether an account still has to finish signup before it may use the page it has landed on. The
 * signup section itself is exempt, since sending it there is the redirect's own destination.
 *
 * Keyed on `signup_complete` rather than an `incomplete` status, so the redirect fires exactly when
 * `RequiresSignupCompleteAttribute` would refuse the request; see `AccountDTO.SignupComplete` for
 * which accounts that admits and why.
 */
export function requiresSignupRedirect(account: Account, basePath: string): boolean {
    return !account.signup_complete && !isWithinSection(basePath, SignupRoute);
}

/**
 * Render the login-redirect page into the app's root, replacing whatever it currently shows.
 *
 * `flushSync` is load-bearing: `onAuthRequired` has already set `window.location` for the login
 * bounce, so the navigation is in flight — without a synchronous commit the redirect page can lose
 * the race with the document unload and never paint, leaving the failed page's error on screen.
 * Guarded by `PanelApp.test.tsx`. See PanelSite.md "React 18 commit timing".
 */
export function renderLoginRedirect(root: Root, appContext: IPanelAppContext): void {
    flushSync(() => {
        root.render(
            <LoginLayout appContext={appContext}>
                <LoginRedirectPage />
            </LoginLayout>
        );
    });
}

export function launchPanelApp<PageProps, ComponentProps extends PageProps>(
    PageComponent:
        | React.FunctionComponent<ComponentProps & Partial<IPanelPageProps>>
        | React.ComponentClass<ComponentProps & Partial<IPanelPageProps>>,
    props: Omit<ComponentProps, keyof PageProps | keyof IPanelPageProps> & Partial<PageProps>,
    LayoutComponent: React.ComponentClass<IPanelLayoutProps> = Layout
) {
    const pageData = getPageData<PageProps>();
    const history = createBrowserHistory();

    const container = document.getElementById('panelApp');
    if (!container) throw new Error("Cannot launch panel app: element '#panelApp' not found");
    // One root serves both the app and the auth-redirect fallback below: the fallback
    // replaces the app's tree in the same container, and React rejects a second
    // createRoot on a container that already has a root.
    const root = createRoot(container);

    const onAuthRequired = createAuthRedirect(window, () => renderLoginRedirect(root, appContext));

    const onNavigate = (url: string, replace?: boolean) => {
        // we use the router's history within this page's SPA, and window.location to leave it
        if (!isInternalNavigation(url, pageData.basePath)) {
            if (replace) {
                window.location.replace(url);
            } else {
                window.location.href = url;
            }
            return;
        }
        if (replace) {
            history.replace(url);
        } else {
            history.push(url);
        }
    };

    const skipAccountLoad = skipsAccountLoad(pageData.basePath);
    const appContext = initAppContext(pageData.appConfig, onNavigate, onAuthRequired, skipAccountLoad);

    if (!skipAccountLoad) {
        void appContext.customer.ensureLoaded().then((account) => {
            // Initialize Zendesk messenger with authenticated user
            void loginZendeskMessenger(() => appContext.siteApiClient.getZendeskMessagingJwt());

            if (requiresSignupRedirect(account, pageData.basePath)) {
                return appContext.navigate(SignupRoute);
            }
        });
    }

    //suppress cancellations
    window.onunhandledrejection = (e: PromiseRejectionEvent) => {
        if (isAbortError(e.reason)) {
            e.preventDefault();
            e.stopPropagation();
            console.info('Request/Promise cancelled', e.promise);
            return false;
        }
    };

    //make sure our basepath matches current path prefix, otherwise redirect to the canonical representation understood by the server
    if (!isWithinSection(window.location.pathname, pageData.basePath)) {
        // replace the base path with the canonical one
        const currentPathSegs = window.location.pathname.split('/');
        // canonical path segments excluding trailing /
        const canonicalBasePathSegs = pageData.basePath.replace(/\/$/, '').split('/');

        const canonicalSegs = canonicalBasePathSegs.concat(currentPathSegs.slice(canonicalBasePathSegs.length));
        const canonicalPath = canonicalSegs.join('/');

        console.info('Redirecting to canonical path', canonicalPath, 'from', window.location.pathname);
        window.location.pathname = canonicalPath;
        return;
    }

    //start the app
    root.render(
        <CustomBrowserRouter history={history} basename={pageData.basePath}>
            <LayoutComponent appContext={appContext}>
                {/* Rendered outside any route, so the page's own <Routes> is the root route tree and
                    relative links resolve against the page's routes (PanelSite.md "Page route trees"). */}
                <ErrorBoundary>
                    <PageComponent appContext={appContext} {...pageData.preload} {...(props as ComponentProps)} />
                </ErrorBoundary>
            </LayoutComponent>
            {!skipAccountLoad && <KeepAlive />}
        </CustomBrowserRouter>
    );
}
